import { useState, useEffect } from 'react';

const UNASSIGNED_FIELDS_ID = '88888888-8888-8888-8888-888888888888';

/**
 * Custom hook to manage crop season selection state
 * Handles both single/limited selection mode and multiple selection mode
 */
const useCropSeasonSelection = (
  allowMultipleSelection,
  cropSeasons,
  isMenuOpen
) => {
  // State for single/limited selection mode
  const [selectedInactiveSeason, setSelectedInactiveSeason] = useState();
  const [selectedActiveSeasons, setSelectedActiveSeasons] = useState([]);

  // State for multiple selection mode
  const [selectedAllSeasons, setSelectedAllSeasons] = useState(
    JSON.parse(localStorage.getItem('selectedCropSeasons')) || []
  );

  const [seasonTitle, setSeasonTitle] = useState('New Crop Season');

  // Helper functions for managing selections
  const handleActiveSeasonChange = (seasonId, checked) => {
    if (allowMultipleSelection) {
      if (checked && !selectedAllSeasons.some(id => seasonId === id)) {
        setSelectedAllSeasons(prev => [...prev, seasonId]);
      } else {
        setSelectedAllSeasons(prev => prev.filter(id => id !== seasonId));
      }
      return;
    }

    if (selectedInactiveSeason) setSelectedInactiveSeason(null);
    if (checked && !selectedActiveSeasons.some(id => seasonId === id)) {
      setSelectedActiveSeasons(prev => [...prev, seasonId]);
    } else {
      setSelectedActiveSeasons(prev => prev.filter(id => id !== seasonId));
    }
  };

  const handleInactiveSeasonChange = (seasonId, checked) => {
    if (allowMultipleSelection) {
      if (checked) {
        setSelectedAllSeasons(prev => [...prev, seasonId]);
      } else {
        setSelectedAllSeasons(prev => prev.filter(id => id !== seasonId));
      }
      return;
    }

    if (selectedActiveSeasons.length) {
      setSelectedActiveSeasons([]);
    }
    setSelectedInactiveSeason(seasonId);
  };

  const handleUnassignedFieldsChange = checked => {
    if (!allowMultipleSelection && checked) {
      if (selectedActiveSeasons.length) {
        setSelectedActiveSeasons([]);
      }
      setSelectedInactiveSeason(UNASSIGNED_FIELDS_ID);
    }
  };

  // Get the final selected seasons for submission
  const getSelectedSeasons = () => {
    if (allowMultipleSelection) {
      return selectedAllSeasons;
    }

    const selectedSeasons = selectedActiveSeasons.length
      ? selectedActiveSeasons
      : [selectedInactiveSeason];
    return selectedSeasons.filter(Boolean);
  };

  // Check if a season is selected
  const isSeasonSelected = seasonId => {
    if (allowMultipleSelection) {
      return selectedAllSeasons.includes(seasonId);
    }

    return (
      selectedActiveSeasons.includes(seasonId) ||
      selectedInactiveSeason === seasonId
    );
  };

  // Helper function to determine season status
  const getSeasonStatus = season => {
    const currentTime = Date.now();
    if (currentTime > season.startDateEpoch) {
      if (currentTime > season.endDateEpoch) {
        return 'past';
      }
      return 'active';
    }
    return 'upcoming';
  };

  // Helper function to select the best inactive season based on priority:
  // 1. First future/upcoming season selected
  // 2. If no future seasons, then first past season selected
  const selectBestInactiveSeason = seasonsToProcess => {
    const inactiveSeasons = cropSeasons.filter(
      season => seasonsToProcess.includes(season.id) && !season.active
    );

    if (inactiveSeasons.length === 0) return null;

    // Separate future and past seasons
    const futureSeasons = inactiveSeasons
      .filter(season => getSeasonStatus(season) === 'upcoming')
      .sort((a, b) => a.name.localeCompare(b.name));
    const pastSeasons = inactiveSeasons
      .filter(season => getSeasonStatus(season) === 'past')
      .sort((a, b) => a.name.localeCompare(b.name));

    // Priority: future seasons first, then past seasons
    if (futureSeasons.length > 0) {
      return futureSeasons[0].id;
    }
    if (pastSeasons.length > 0) {
      return pastSeasons[0].id;
    }

    return null;
  };

  // Apply selections and save to localStorage
  const applySelections = () => {
    const selectedCropSeasons = getSelectedSeasons();
    localStorage.setItem(
      'selectedCropSeasons',
      JSON.stringify(selectedCropSeasons)
    );
    return selectedCropSeasons;
  };

  // Cancel selections and revert to localStorage state
  const cancelSelections = () => {
    const selectedCropSeasons =
      JSON.parse(localStorage.getItem('selectedCropSeasons')) || [];

    if (allowMultipleSelection) {
      setSelectedAllSeasons(
        selectedCropSeasons.filter(id => id !== UNASSIGNED_FIELDS_ID)
      );
      setSelectedInactiveSeason(null);
      setSelectedActiveSeasons([]);
      return;
    }

    // Revert to single selection mode state from localStorage
    const seasonsToProcess = selectedCropSeasons;
    const activeSeasons = cropSeasons
      .filter(season => seasonsToProcess.includes(season.id) && season.active)
      .map(season => season.id);

    if (activeSeasons.length > 0) {
      setSelectedActiveSeasons([activeSeasons[0]]);
      setSelectedInactiveSeason(null);
    } else {
      const bestInactiveSeason = selectBestInactiveSeason(seasonsToProcess);
      if (bestInactiveSeason) {
        setSelectedInactiveSeason(bestInactiveSeason);
        setSelectedActiveSeasons([]);
      } else {
        setSelectedInactiveSeason(UNASSIGNED_FIELDS_ID);
        setSelectedActiveSeasons([]);
      }
    }

    setSelectedAllSeasons([]);
  };

  // Initialize selection state from localStorage
  useEffect(() => {
    if (!cropSeasons.length) return;

    const selectedCropSeasons =
      JSON.parse(localStorage.getItem('selectedCropSeasons')) || [];

    if (allowMultipleSelection) {
      // In multiple selection mode, all selected seasons go into selectedAllSeasons
      setSelectedAllSeasons(
        selectedCropSeasons.filter(id => id !== UNASSIGNED_FIELDS_ID)
      );
      // Clear single selection states when in multiple mode
      setSelectedInactiveSeason(null);
      setSelectedActiveSeasons([]);
      return;
    }

    // When transitioning from multiple to single selection mode
    // Use the current selectedAllSeasons if it has values, otherwise use localStorage
    const seasonsToProcess =
      selectedAllSeasons.length > 0 ? selectedAllSeasons : selectedCropSeasons;

    // Single/limited selection mode logic
    const activeSeasons = cropSeasons
      .filter(season => seasonsToProcess.includes(season.id) && season.active)
      .map(season => season.id);

    // Priority: Active seasons first, then inactive seasons, then default to unassigned
    if (activeSeasons.length > 0) {
      setSelectedActiveSeasons(activeSeasons);
      setSelectedInactiveSeason(null);
    } else {
      const bestInactiveSeason = selectBestInactiveSeason(seasonsToProcess);
      if (bestInactiveSeason) {
        setSelectedInactiveSeason(bestInactiveSeason);
        setSelectedActiveSeasons([]);
      } else {
        // Default to unassigned fields
        setSelectedInactiveSeason(UNASSIGNED_FIELDS_ID);
        setSelectedActiveSeasons([]);
      }
    }

    // Clear multiple selection state when in single mode
    setSelectedAllSeasons([]);

    // Update localStorage with the new selection after mode transition
    // We need to use a setTimeout to ensure state updates have been applied
    setTimeout(() => {
      const currentSelectedSeasons = allowMultipleSelection
        ? selectedCropSeasons.filter(id => id !== UNASSIGNED_FIELDS_ID)
        : (() => {
            if (activeSeasons.length > 0) {
              return activeSeasons;
            }
            const bestInactiveSeason = selectBestInactiveSeason(
              seasonsToProcess
            );
            return bestInactiveSeason
              ? [bestInactiveSeason]
              : [UNASSIGNED_FIELDS_ID];
          })();

      localStorage.setItem(
        'selectedCropSeasons',
        JSON.stringify(currentSelectedSeasons)
      );
    }, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowMultipleSelection, cropSeasons, isMenuOpen]);

  useEffect(() => {
    const selectedCropSeasons = getSelectedSeasons();

    const seasonsSelected =
      selectedCropSeasons.length &&
      selectedCropSeasons[0] !== '88888888-8888-8888-8888-888888888888'
        ? cropSeasons
            ?.filter(season => selectedCropSeasons.includes(season.id))
            .reduce((season, val) => {
              const pipe = season.length ? ' | ' : '';
              return season + pipe + val.name;
            }, '')
        : 'New Crop Season';
    setSeasonTitle(seasonsSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cropSeasons,
    allowMultipleSelection,
    selectedActiveSeasons,
    selectedInactiveSeason,
    selectedAllSeasons
  ]);

  return {
    selectedInactiveSeason,
    selectedActiveSeasons,
    selectedAllSeasons,
    handleActiveSeasonChange,
    handleInactiveSeasonChange,
    handleUnassignedFieldsChange,
    getSelectedSeasons,
    isSeasonSelected,
    seasonTitle,
    applySelections,
    cancelSelections
  };
};

export default useCropSeasonSelection;
